import React, { useState } from "react";
import { createSubscription, closeCurrentWidget } from "../../utils/payments";
import { HapticFeedback } from "../../utils/hapticFeedback";
import { useTheme } from "../../hooks/useTheme";
import styles from "./PayWall.module.css";
import { useEffect } from "react";
import paywallLight from "../icons/payWall/light/payWal.png";
import paywallDark from "../icons/payWall/dark/payWal.png";

interface PaywallProps {
  isOpen: boolean;
  onClose: () => void;
  onSubscriptionSuccess?: () => void;
  cloudPaymentsPublicKey: string;
  userId: string;
  tariff?: { month: number; half_year: number; year: number } | null;
}

export const Paywall: React.FC<PaywallProps> = ({
  isOpen,
  onClose,
  onSubscriptionSuccess,
  cloudPaymentsPublicKey,
  userId,
  tariff,
}) => {
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("6month"); // По умолчанию 6 месяцев

  const monthPrice = tariff?.month;
  const halfYearPrice = tariff?.half_year;
  const yearPrice = tariff?.year;

  const isTestPrice = monthPrice === 1;

  const halfYearDiscount = Math.round(
    (1 - halfYearPrice! / 6 / monthPrice!) * 100
  );
  const yearDiscount = Math.round((1 - yearPrice! / 12 / monthPrice!) * 100);

  const allPlans = [
    {
      id: "1month",
      title: "На 1 месяц",
      price: `${monthPrice} ₽/мес`,
      originalPrice: null,
      discount: null,
    },
    {
      id: "6month",
      title: "На 6 месяцев",
      price: `${Math.round(halfYearPrice! / 6)} ₽/мес`,
      originalPrice: `${monthPrice! * 6} ₽`,
      savedPrice: `${halfYearPrice} ₽`,
      discount: `выгода ${halfYearDiscount}%`,
    },
    {
      id: "12month",
      title: "Годовая подписка",
      price: `${Math.round(yearPrice! / 12)} ₽/мес`,
      originalPrice: `${monthPrice! * 12} ₽`,
      savedPrice: `${yearPrice} ₽`,
      discount: `выгода ${yearDiscount}%`,
    },
  ];

  const plans = isTestPrice ? [allPlans[0]] : allPlans;

  useEffect(() => {
    if (isTestPrice) {
      setSelectedPlan("1month");
    }
  }, [isTestPrice]);

  const features = [
    "Получай уведомления о постах ДПС",
    "Читай сводки от других водителей",
    "Следи за камерами в реальном времени",
    "Знакомься с людьми из своего города",
    "Участвуй в конкурсах и активностях",
  ];

  useEffect(() => {
    if (!isOpen) return;

    const tg = window.Telegram?.WebApp;
    if (!tg) return;

    tg.BackButton.show();

    const handleBackButton = () => {
      const widgetClosed = closeCurrentWidget();

      if (widgetClosed) {
        HapticFeedback.light();
        setIsLoading(false);
      } else {
        HapticFeedback.light();
        onClose();
      }
    };

    tg.BackButton.onClick(handleBackButton);

    return () => {
      tg.BackButton.hide();
      tg.BackButton.offClick(handleBackButton);
    };
  }, [isOpen, onClose]);

  const handleSubscribe = async (): Promise<void> => {
    if (isLoading) return;

    setIsLoading(true);
    HapticFeedback.medium();

    const planConfig = {
      "1month": {
        amount: monthPrice,
        period: 1,
        description: "ДПС-Контроль Premium - 1 месяц",
      },
      "6month": {
        amount: halfYearPrice,
        period: 6,
        description: "ДПС-Контроль Premium - 6 месяцев",
      },
      "12month": {
        amount: yearPrice,
        period: 12,
        description: "ДПС-Контроль Premium - 12 месяцев",
      },
    } as const;

    const currentPlan = planConfig[selectedPlan as keyof typeof planConfig];

    try {
      await createSubscription({
        publicKey: cloudPaymentsPublicKey,
        userId: userId,
        amount: currentPlan.amount,
        period: currentPlan.period,
        description: currentPlan.description,
        onSuccess: (options: any) => {
          console.log("Подписка успешно оформлена:", options);
          HapticFeedback.success();

          if (onSubscriptionSuccess) {
            onSubscriptionSuccess();
          } else {
            window.location.reload();
          }
        },
        onError: (reason: any, options: any) => {
          console.error("Ошибка оплаты:", reason, options);
          setIsLoading(false);

          if (reason === "widget_closed" || reason === "User has cancelled") {
            console.log("Пользователь закрыл окно оплаты");
          } else {
            console.error("Ошибка при оформлении подписки:", reason);
            HapticFeedback.error();
          }
        },
      });
    } catch (error) {
      console.error("Ошибка при инициализации оплаты:", error);
      HapticFeedback.error();
      setIsLoading(false);
      console.error("Не удалось инициализировать CloudPayments:", error);
    }
  };

  const handleClose = () => {
    HapticFeedback.light();
    onClose();
  };

  if (!isOpen) return null;

  const paywallImage = theme === "dark" ? paywallDark : paywallLight;

  return (
    <div
      className={`${styles.overlay} ${
        theme === "dark" ? styles.dark : styles.light
      }`}
    >
      <div className={styles.container}>
        {/* Контент с прокруткой */}
        <div className={styles.scrollContent}>
          {/* Изображение пейвола */}
          <div className={styles.imageContainer}>
            {/* Кнопка закрытия */}
            <button className={styles.closeButton} onClick={handleClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <img
              src={paywallImage}
              alt="ДПС-Контроль Premium"
              className={styles.paywallImage}
            />
          </div>

          {/* Основной контент на фоне */}
          <div className={styles.contentBackground}>
            {/* Заголовок */}
            <div className={styles.titleContainer}>
              <h2 className={styles.title}>Открой все возможности</h2>
              <h2 className={styles.title}>по подписке Premium</h2>
            </div>

            {/* Список функций */}
            <div className={styles.featuresContainer}>
              {features.map((feature, index) => (
                <div key={index} className={styles.feature}>
                  <svg
                    className={styles.checkIcon}
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M16.25 6.25L8.75 13.75L3.75 8.75"
                      stroke="#8B5CF6"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className={styles.featureText}>{feature}</span>
                </div>
              ))}
            </div>

            <div className={styles.plansContainer}>
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`${styles.planCard} ${
                    selectedPlan === plan.id ? styles.planSelected : ""
                  }`}
                  onClick={() => {
                    if (plan.id === "1month") {
                      HapticFeedback.light();
                    } else if (plan.id === "6month") {
                      HapticFeedback.medium();
                    } else if (plan.id === "12month") {
                      HapticFeedback.heavy();
                    }
                    setSelectedPlan(plan.id);
                  }}
                >
                  <div className={styles.planHeader}>
                    <div className={styles.planInfo}>
                      <div className={styles.planTitle}>{plan.title}</div>
                      {plan.originalPrice && (
                        <div className={styles.price}>
                          <span className={styles.originalPrice}>
                            {" "}
                            {plan.originalPrice}{" "}
                          </span>
                          <span className={styles.planSavedPrice}>
                            {plan.savedPrice}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className={styles.planPricing}>
                      <div className={styles.planPrice}>{plan.price}</div>
                      {plan.discount && (
                        <div className={styles.planDiscount}>
                          {plan.discount}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Кнопка подписки */}
            <div className={styles.buttonContainer}>
              <button
                className={styles.subscribeButton}
                onClick={handleSubscribe}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className={styles.spinner} />
                    <span>Подключение...</span>
                  </>
                ) : (
                  "Подписаться"
                )}
              </button>
            </div>

            {/* Ссылки на документы */}
            <div className={styles.linksContainer}>
              <a
                href="https://dpscontrol.ru/docs/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.documentLink}
              >
                Политика
              </a>
              <a
                href="https://dpscontrol.ru/docs/offer"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.documentLink}
              >
                Оферта
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
